import { Schema } from "@upserve/cold-storage";
import { addDays, format, getDay } from "date-fns";
import { map, omit, reduce } from "ramda";
import { Maybe } from "true-myth";
import { default as makeUuid } from "uuid/v4";

import {
  dateToWeekday,
  differenceInWeekdays,
  DateFormats,
  Weekday,
} from "helpers/dates";
import { maybetype, excludeEmptySchema } from "helpers/schema";
import { splitShiftDate, buildShiftDate } from "helpers/transforms/blockDates";
import { removePersonFromSchedule } from "helpers/transforms/people";
import {
  mergePeopleIntoSchedule,
  posUserSchema,
  roleSchema,
  splitScheduleByDays,
  ITransformedResponse,
  IShiftWithMergedDate,
} from "helpers/transforms/schedule";
import { ITemplateResponse } from "interfaces/template";
import {
  ISchedule,
  IPerson,
  IPeople,
  IShift,
  IUnavailabilityBlock,
} from "types/schedules";
import {
  ITemplate,
  ITemplateSchedule,
  ITemplatePerson,
  ITemplateShift,
} from "types/templates";

const { parseBySchema, jsontype, encodeBySchema } = Schema;

function scheduleToTemplate(
  schedule: ISchedule,
  name: string,
  uuid: string = ""
): ITemplate {
  // Build a schedule where all shifts are Unassigned
  const unassignedSchedule: ISchedule = reduce(
    (finalSchedule: ISchedule, assignedPerson: IPerson) => {
      const newUnassignedShiftGroup: IPerson = {
        ...assignedPerson,
        assignee: Maybe.nothing(),
      };
      return mergePeopleIntoSchedule(
        [newUnassignedShiftGroup],
        removePersonFromSchedule(finalSchedule, assignedPerson)
      );
    },
    schedule,
    schedule.people
  );

  // Given this schedule of unassigned people, split into days
  const scheduleDays = splitScheduleByDays(unassignedSchedule);
  const templateScheduleDays: ITemplateSchedule[] = scheduleDays.map(
    (scheduleDay: ITransformedResponse) => {
      return {
        dayOfWeek: dateToWeekday(scheduleDay.date), // Day of week written out
        people: map(personToTemplatePerson, scheduleDay.people),
        updatedAt: Maybe.nothing<Date>(),
      };
    }
  );
  return {
    name,
    schedules: templateScheduleDays,
    uuid,
    updatedAt: Maybe.nothing(),
  };
}

const ORDERED_WEEKDAYS: Weekday[] = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
function templateToSchedule(template: ITemplate, startDate: Date): ISchedule {
  const emptySchedule: ISchedule = {
    startDate: Maybe.of(startDate),
    endDate: Maybe.of(addDays(startDate, 6)),
    updatedAt: Maybe.nothing(),
    people: [],
  };
  const scheduleStartsOn = getDay(startDate);

  return reduce(
    (populatedSchedule: ISchedule, templateDay: ITemplateSchedule) => {
      const thisWeekday = ORDERED_WEEKDAYS.indexOf(templateDay.dayOfWeek);
      // Find how many days into the schedule this weekday would be placed.
      const daysIntoSchedule = differenceInWeekdays(
        scheduleStartsOn,
        thisWeekday
      );
      const thisDate = addDays(startDate, daysIntoSchedule);
      const people: IPeople = map(
        (templatePerson) => templatePersonToPerson(templatePerson, thisDate),
        templateDay.people
      );
      return mergePeopleIntoSchedule(people, populatedSchedule);
    },
    emptySchedule,
    template.schedules
  );
}

function templateShiftToShift(
  templateShift: ITemplateShift,
  date: Date
): IShift {
  const uuid = makeUuid();
  const shiftWithMergedDate: IShiftWithMergedDate = {
    ...templateShift,
    date: format(date, DateFormats.YEAR_MONTH_DAY),
    uuid,
  };
  const startTime = buildShiftDate(shiftWithMergedDate, "start");
  const endTime = buildShiftDate(shiftWithMergedDate, "end");
  return {
    role: templateShift.role,
    startTime,
    endTime,
    uuid,
  };
}

function personToTemplatePerson(person: IPerson): ITemplatePerson {
  return {
    ...person,
    shifts: map((shift) => {
      const { startTimeHour, startTimeMinute } = splitShiftDate(
        shift.startTime,
        "start"
      );
      const { endTimeHour, endTimeMinute } = splitShiftDate(
        shift.endTime,
        "end"
      );
      return {
        ...omit(["startTime", "endTime"], shift),
        startTimeHour,
        startTimeMinute,
        endTimeHour,
        endTimeMinute,
      };
    }, person.shifts),
  };
}

function templatePersonToPerson(
  templatePerson: ITemplatePerson,
  date: Date
): IPerson {
  return {
    ...templatePerson,
    shifts: map(
      (shift) => templateShiftToShift(shift, date),
      templatePerson.shifts
    ),
    unavail: Maybe.nothing<IUnavailabilityBlock[]>(),
  };
}

function emptyScheduleOfShifts(schedule: ISchedule): ISchedule {
  const emptyPeople = map(
    (person: IPerson) => ({
      ...person,
      shifts: [],
      unavail: Maybe.nothing<IUnavailabilityBlock[]>(),
    }),
    schedule.people
  );
  return {
    ...schedule,
    people: emptyPeople,
  };
}

// Schema
const shiftSchema: ITemplateShift = {
  role: roleSchema,
  startTimeHour: jsontype("integer"),
  startTimeMinute: jsontype("integer"),
  endTimeHour: jsontype("integer"),
  endTimeMinute: jsontype("integer"),
};

const templatePersonSchema: ITemplatePerson = {
  assignee: maybetype(posUserSchema),
  shifts: [shiftSchema],
};

const templateScheduleSchema: ITemplateSchedule = {
  dayOfWeek: jsontype("string") as Weekday,
  people: [templatePersonSchema],
};

const templateSchema: ITemplate = {
  uuid: excludeEmptySchema(),
  name: jsontype("string"),
  schedules: [templateScheduleSchema],
  updatedAt: maybetype(jsontype("dateTime")),
};

function parseTemplateSchema(templateResponse: ITemplateResponse): ITemplate {
  return parseBySchema(templateSchema)(templateResponse);
}

const encodeTemplateToJSON = encodeBySchema(templateSchema);

export {
  parseTemplateSchema,
  encodeTemplateToJSON,
  scheduleToTemplate,
  templateToSchedule,
  emptyScheduleOfShifts,
};
